import { AUTH_ENABLED } from './authConfig';
import { useUser } from '@clerk/clerk-react';
import { isAtLeast as tierIsAtLeast, hasFeature as tierHasFeature } from './tierPolicy';

/**
 * AUTH-5 — the UI tier hook (the Stripe-later seam, frontend half). Bound at
 * module load to the enabled or disabled implementation by the build-time flag,
 * so no hook is ever called conditionally (rules-of-hooks safe) and the disabled
 * build has no ClerkProvider dependency.
 *
 * Returns `{ tier, isPaid, isAdmin, isLoaded, isSignedIn, userEmail, isAtLeast,
 * hasFeature }`. Mirrors the backend getTier default (free unless publicMetadata
 * says otherwise). `isPaid`/`isAdmin` are kept for existing consumers
 * (NavigationBar, TierGate, PaidAction, UpgradeScreen) with byte-identical
 * semantics — `isPaid` is now literally `isAtLeast('paid')`, which agrees with
 * the old `tier === 'paid' || tier === 'admin'` formula for every tier value
 * that existed before this refactor, and extends correctly to the new
 * 'enterprise' rank (TIER-3 Phase 0, design doc §6.1) without a call-site
 * change. `isAtLeast(minTier)`/`hasFeature(feature)` are the option-independent
 * refactor required before any Enterprise-gated surface — nothing consumes them
 * yet (Phase 0 ships no Enterprise route), they exist so that first surface is
 * additive, not a redesign. Use for UI gating only — the server
 * (requireTier/requireAdmin/requireFeature) is the real boundary. See ADR-007.
 * `userEmail` (Clerk's primary email, '' if unresolved/signed out) is the single
 * Clerk read that request-access prefill callers (TierGate, PaidAction) rely on
 * — keeping it here means those callers stay Clerk-import-free.
 */

function useTierEnabled() {
    const { isLoaded, isSignedIn, user } = useUser();
    const raw = isSignedIn && user ? user.publicMetadata?.tier : undefined;
    const tier = (raw === 'admin' || raw === 'paid' || raw === 'enterprise') ? raw : 'free';
    const userEmail = user?.primaryEmailAddress?.emailAddress ?? '';
    return {
        tier,
        isPaid: tierIsAtLeast(tier, 'paid'),
        isAdmin: tier === 'admin',
        isLoaded,
        isSignedIn: !!isSignedIn,
        userEmail,
        isAtLeast: (minTier) => tierIsAtLeast(tier, minTier),
        hasFeature: (feature) => tierHasFeature(tier, feature),
    };
}

function useTierDisabled() {
    return {
        tier: 'free',
        isPaid: false,
        isAdmin: false,
        isLoaded: true,
        isSignedIn: false,
        userEmail: '',
        isAtLeast: (minTier) => tierIsAtLeast('free', minTier),
        hasFeature: (feature) => tierHasFeature('free', feature),
    };
}

export const useTier = AUTH_ENABLED ? useTierEnabled : useTierDisabled;

export default useTier;
